import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { DateTime, Settings } from 'luxon';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { useCart } from '../contexts/CartContext';
import SurveySection from '../components/SurveySection';
import {
  MESSAGE_ERREUR_SERVEUR,
  apprenantPourServeur,
  articlePourVerification,
  verifierAupresDuServeur,
  messagesDeReponse,
  defilerVersPremiereErreur,
  texteTelephones,
} from '../utils/reglesInscription';

// Configuration globale de Luxon pour utiliser UTC
Settings.defaultZone = 'utc';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, clearCart, setCartItems } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [showSurvey] = useState(true);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('interac'); // 'interac' | 'stripe'
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [expandedItems, setExpandedItems] = useState({}); // détails apprenant repliés par défaut
  const [successEmail, setSuccessEmail] = useState(''); // email conservé pour le sondage post-confirmation

  // « Facturer à » : la personne qui paie, au nom de qui la facture est émise.
  // Préremplie avec le payeur saisi dans le formulaire d'inscription (enfant)
  // ou avec le premier inscrit, et modifiable (ex. : un CPE qui paie pour une
  // employée). Tant que la personne n'y a pas touché, elle suit le panier.
  const [payeur, setPayeur] = useState({ prenom: '', nom: '', organisme: '', courriel: '', telephone: '' });
  const [payeurModifie, setPayeurModifie] = useState(false);
  const [erreursPayeur, setErreursPayeur] = useState({});

  useEffect(() => {
    if (payeurModifie || cartItems.length === 0) return;
    const premier = cartItems[0].formData || {};
    const pourUnTiers = !cartItems[0].isForSelf && (premier.prenom_payeur || premier.nom_payeur);
    setPayeur({
      prenom: (pourUnTiers ? premier.prenom_payeur : premier.prenom) || '',
      nom: (pourUnTiers ? premier.nom_payeur : premier.nom) || '',
      organisme: '',
      courriel: premier.email || '',
      telephone: premier.telephone || '',
    });
  }, [cartItems, payeurModifie]);

  const changerPayeur = (champ) => (e) => {
    setPayeurModifie(true);
    setPayeur((p) => ({ ...p, [champ]: e.target.value }));
    setErreursPayeur((er) => ({ ...er, [champ]: undefined }));
  };

  const payeurPropre = () => Object.fromEntries(Object.entries(payeur).map(([k, v]) => [k, String(v || '').trim()]));

  const verifierPayeur = () => {
    const p = payeurPropre();
    const erreurs = {};
    if (!p.prenom && !p.nom && !p.organisme) erreurs.nom = 'Indiquez le nom de la personne ou de l\'organisme qui paie';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.courriel)) erreurs.courriel = 'Courriel invalide : la facture y sera envoyée';
    setErreursPayeur(erreurs);
    return Object.keys(erreurs).length === 0;
  };

  // ── Double vérification avant le paiement ──────────────────────────────
  // Erreurs par article du panier : { [item.id]: [messages] }.
  const [erreursArticles, setErreursArticles] = useState({});
  const [verificationEnCours, setVerificationEnCours] = useState(false);

  // Un article retiré emporte ses erreurs.
  useEffect(() => {
    setErreursArticles((prev) => {
      const restantes = Object.fromEntries(Object.entries(prev).filter(([id]) => cartItems.some((i) => String(i.id) === id)));
      return Object.keys(restantes).length === Object.keys(prev).length ? prev : restantes;
    });
  }, [cartItems]);

  const moyenDuServeur = () => (paymentMethod === 'stripe' ? 'carte' : 'interac');

  // Affiche le résultat d'une vérification du serveur sur les articles du
  // panier. Renvoie true si tout est valide.
  const appliquerVerification = (verification) => {
    const erreurs = {};
    (verification.articles || []).forEach((article) => {
      const item = cartItems[article.index];
      if (item && article.erreurs?.length) erreurs[item.id] = article.erreurs.map((e) => e.message);
    });
    setErreursArticles(erreurs);
    const nombre = Object.keys(erreurs).length;
    if (nombre > 0) {
      setError(
        `Paiement impossible pour le moment : ${nombre} inscription${nombre > 1 ? 's' : ''} du panier ${nombre > 1 ? 'doivent' : 'doit'} être ${nombre > 1 ? 'corrigées' : 'corrigée'} (détail en rouge ci-dessous). Aucun montant n'a été débité.`
      );
      defilerVersPremiereErreur();
    }
    return nombre === 0;
  };

  // Vérifie tout le panier auprès du serveur. Le paiement n'est possible que
  // si cette vérification passe.
  const verifierPanier = async () => {
    try {
      const verification = await verifierAupresDuServeur(API_URL, cartItems.map(articlePourVerification), moyenDuServeur());
      return appliquerVerification(verification);
    } catch (erreurVerification) {
      setError(erreurVerification.message);
      defilerVersPremiereErreur();
      return false;
    }
  };

  // Première vérification : avant même d'ouvrir la fenêtre de confirmation.
  const ouvrirConfirmation = async () => {
    setError('');
    setErreursPayeur({});
    setVerificationEnCours(true);
    const valide = await verifierPanier();
    setVerificationEnCours(false);
    if (valide) setConfirmDialogOpen(true);
  };

  // Interac : la personne qui paie est demandée dans la fenêtre de
  // confirmation. Carte : la page de paiement Stripe la recueille elle-même.
  const confirmerDialogue = () => {
    if (paymentMethod === 'interac' && !verifierPayeur()) return;
    handleConfirmPayment();
  };

  const toggleItemDetails = (id) =>
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Vérifier si le paiement par carte (Stripe) est activé par l'admin
  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/maintenance`);
        const enabled = data?.stripeEnabled !== false;
        setStripeEnabled(enabled);
        // Si Stripe est désactivé, on force le paiement par Interac
        if (!enabled) setPaymentMethod('interac');
      } catch (err) {
        // En cas d'erreur, on garde Stripe disponible par défaut
        console.error('Erreur récupération du statut Stripe:', err);
      }
    };
    fetchStripeStatus();
  }, [API_URL]);

  // Taux de taxes québécoises
  const TPS_RATE = 0.05; // TPS: 5%
  const TVQ_RATE = 0.09975; // TVQ: 9.975% (appliquée sur le sous-total + TPS)

  // Calculer les taxes
  const calculateTaxes = (subtotal) => {
    const tps = subtotal * TPS_RATE;
    const tvq = subtotal * TVQ_RATE;
    return {
      subtotal,
      tps,
      tvq,
      total: subtotal + tps + tvq
    };
  };

  // Formater la date pour l'affichage (utilisé pour les dates de naissance)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Extraire la partie date si c'est une chaîne ISO complète
      const cleanDate = dateString.split('T')[0];
      // Créer un objet DateTime à partir de la date (interprétée en UTC)
      const dt = DateTime.fromISO(cleanDate);
      // Formater en français (jj mois aaaa)
      return dt.toFormat('dd MMMM yyyy', { locale: 'fr' });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };

  // Facture « en attente » et courriel de confirmation Interac. Le serveur
  // notifie lui-même l'Académie (avec la facture jointe) au même moment :
  // voir backend/src/controllers/factureInscriptionController.js.
  // Le serveur relit les inscriptions tout juste créées et calcule lui-même les
  // montants (code rabais compris) : le navigateur n'envoie que les numéros
  // d'inscription et la personne à facturer.
  const envoyerFactureInterac = async (resultats) => {
    try {
      await axios.post(`${API_URL}/factures/interac`, {
        inscriptions: resultats.map((r) => ({ id: r.inscriptionId, rabaisCode: r.item.rabais?.code || null })),
        payeur: payeurPropre(),
      });
    } catch (err) {
      // Non bloquant : les inscriptions sont enregistrées ; l'Académie peut
      // renvoyer la facture depuis l'administration.
      console.error('Facture Interac non envoyée :', err.response?.data || err);
    }
  };

  // Traiter la confirmation du paiement (fake payment)
  const handleConfirmPayment = async () => {
    setLoading(true);
    setError('');
    
    try {
      // ── STRIPE : on ne crée RIEN en base avant le paiement ──────────────
      // On envoie le panier au backend qui le stocke temporairement et crée la
      // session Stripe. Les apprenants/inscriptions ne sont créés qu'APRÈS un
      // paiement réussi (sur la page de retour). Si le client abandonne, rien
      // n'est enregistré.
      if (paymentMethod === 'stripe') {
        const orderItems = cartItems.map((item) => ({
          apprenant: apprenantPourServeur(item.formData, item.isForSelf),
          email: item.formData.email,
          session_id: item.sessionId,
          pour_un_tiers: !item.isForSelf,
          formationTitre: item.formationTitre,
          nom_du_groupe: item.nom_du_groupe || null,
          rabaisCode: item.rabais?.code || null,
        }));

        // Seconde vérification : le serveur revérifie tout le panier avant de
        // créer la page de paiement ; s'il refuse, personne n'est débité.
        let checkoutResponse;
        try {
          checkoutResponse = await axios.post(`${API_URL}/payment/create-checkout-session`, {
            items: orderItems,
            customerEmail: cartItems[0]?.formData?.email,
          });
        } catch (erreurPaiement) {
          setConfirmDialogOpen(false);
          const verification = erreurPaiement.response?.data?.verification;
          if (verification) {
            appliquerVerification(verification);
          } else {
            setError(messagesDeReponse(erreurPaiement).join(' '));
            defilerVersPremiereErreur();
          }
          return;
        }

        if (checkoutResponse.data?.success && checkoutResponse.data.url) {
          // Mémoriser l'email pour afficher le sondage au retour (indépendant du backend)
          localStorage.setItem('vs_survey_email', cartItems[0]?.formData?.email || '');
          window.location.href = checkoutResponse.data.url;
          return;
        }

        setError("Impossible d'initier le paiement par carte. Veuillez réessayer.");
        setConfirmDialogOpen(false);
        return;
      }

      // ── INTERAC : création immédiate (statut "en attente"), comme avant ──
      // Seconde vérification juste avant de créer quoi que ce soit : si le
      // panier a changé côté serveur (séance complète…), rien n'est créé.
      if (!(await verifierPanier())) {
        setConfirmDialogOpen(false);
        return;
      }

      // Traiter chaque article du panier
      const results = [];

      for (const item of cartItems) {
        try {
          const apprenantPayload = apprenantPourServeur(item.formData, item.isForSelf);

          const apprenantResponse = await axios.post(`${API_URL}/apprenants`, apprenantPayload);
          
          if (apprenantResponse.data.success) {
            const apprenantId = apprenantResponse.data.data.id;
            
            const inscriptionData = {
              participant_id: apprenantId,
              session_id: item.sessionId,
              type_inscription: 'individuel',
              statut: 'en attente',
              nom_du_groupe: item.nom_du_groupe || null,
            };
            
            const inscriptionResponse = await axios.post(`${API_URL}/inscriptions`, inscriptionData);
            
            if (inscriptionResponse.data && inscriptionResponse.data.success) {
              // Marquer la préinscription correspondante comme finalisée (discret).
              // La personne a confirmé : on la "raye" de la liste sans la supprimer.
              axios.patch(`${API_URL}/preinscriptions/finalize`, {
                email: item.formData.email,
                session_id: item.sessionId,
                apprenant_id: apprenantId,
                inscription_id: inscriptionResponse.data.data.id,
              }).catch((finErr) => console.error('Finalisation préinscription (non bloquant):', finErr));

              results.push({ success: true, item, inscriptionId: inscriptionResponse.data.data.id });
            } else {
              results.push({ success: false, item, messages: [MESSAGE_ERREUR_SERVEUR] });
            }
          } else {
            results.push({ success: false, item, messages: [MESSAGE_ERREUR_SERVEUR] });
          }
        } catch (itemError) {
          console.error('Erreur pour cet article:', itemError);
          results.push({ success: false, item, messages: messagesDeReponse(itemError) });
        }
      }

      // Vérifier si tous les articles ont été traités avec succès
      const allSuccessful = results.every(r => r.success);

      if (!allSuccessful) {
        // Les inscriptions enregistrées quittent le panier et reçoivent leur
        // facture ; les autres restent, avec leur erreur lisible.
        const reussies = results.filter(r => r.success);
        const echouees = results.filter(r => !r.success);
        if (reussies.length > 0) {
          await envoyerFactureInterac(reussies);
          setCartItems(items => items.filter(i => !reussies.some(r => r.item.id === i.id)));
        }
        setErreursArticles(Object.fromEntries(echouees.map(r => [r.item.id, r.messages])));
        setError(
          (reussies.length > 0
            ? `${reussies.length} inscription(s) enregistrée(s) : un courriel de confirmation a été envoyé. `
            : '')
          + `${echouees.length} inscription(s) n'ont pas pu être enregistrée(s) (détail en rouge ci-dessous).`
        );
        setConfirmDialogOpen(false);
        defilerVersPremiereErreur();
        return;
      }

      if (allSuccessful) {
        // Paiement par Interac (le seul cas qui arrive ici — Stripe est traité plus haut)
        // Une seule facture pour tout le panier ; le serveur notifie aussi
        // l'Académie au même moment (facture jointe, vrai payeur).
        await envoyerFactureInterac(results);

        // Conserver l'email avant de vider le panier (pour le sondage post-confirmation)
        setSuccessEmail(cartItems[0]?.formData?.email || '');
        setSuccess(true);
        clearCart();
        setConfirmDialogOpen(false);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setConfirmDialogOpen(false);
      setError(messagesDeReponse(err).join(' '));
      defilerVersPremiereErreur();
    } finally {
      setLoading(false);
    }
  };

  // Affichage de succès
  if (success) {
    return (
      <>
        <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 3 }} />
          
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            Inscription confirmée!
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
            Merci ! Votre inscription{cartItems.length > 1 ? 's' : ''} {cartItems.length > 1 ? 'ont' : 'a'} bien été enregistrée{cartItems.length > 1 ? 's' : ''}.
          </Typography>
          
         <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
  📧 Un courriel de confirmation vous a été envoyé avec le détail du paiement.{' '}
  <strong>Veuillez vérifier votre boîte de réception ainsi que vos courriels indésirables.</strong>
</Typography>

          {showSurvey && !surveySubmitted && successEmail && (
            <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'left', mt: 2 }}>
              <SurveySection
                email={successEmail}
                onSurveySubmitted={() => setSurveySubmitted(true)}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 5 }}>
            <Button
              component={Link}
              to="/formations"
              variant="contained"
              sx={{ minWidth: 200 }}
            >
              Voir d'autres formations
            </Button>
            
            <Button
              component={Link}
              to="/"
              variant="outlined"
              sx={{ minWidth: 200 }}
            >
              Retour à l'accueil
            </Button>
          </Box>
        </Box>
      </Container>
    </>
    );
  }

  // Affichage du panier vide
  if (cartItems.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: '#999', mb: 3 }} />
          
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            Panier vide
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
            Vous n'avez pas encore d'inscriptions en attente.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              component={Link}
              to="/formations"
              variant="contained"
              sx={{ minWidth: 200 }}
            >
              Explorer les formations
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Totaux (utilisés par le résumé et la barre mobile)
  const cartSubtotal = cartItems.reduce((sum, item) => sum + (item.formation?.tarif || 0), 0);
  const cartTaxes = calculateTaxes(cartSubtotal);

  // Affichage du panier avec articles
  return (
    <Container maxWidth="lg" sx={{ py: 4, pb: { xs: 14, md: 4 } }}>
      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            backgroundColor: 'rgba(198, 40, 40, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(198, 40, 40, 0.2)',
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Mon Panier
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {/* LAYOUT EN 2 COLONNES */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>

      {/* COLONNE GAUCHE : articles + sondage */}
      <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>

      {/* ARTICLES DU PANIER */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {cartItems.map((item, index) => (
          <Card
            key={item.id}
            data-erreur={erreursArticles[item.id] ? 'true' : undefined}
            sx={{ position: 'relative', ...(erreursArticles[item.id] ? { border: '2px solid', borderColor: 'error.main' } : {}) }}
          >
            <CardContent>
              {/* Erreurs de cette inscription, renvoyées par la vérification du serveur */}
              {erreursArticles[item.id] && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {[item.formData?.prenom, item.formData?.nom].filter(Boolean).join(' ') || 'Cette inscription'} : à corriger
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {erreursArticles[item.id].map((message) => (
                      <li key={message}><Typography variant="body2">{message}</Typography></li>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Un article du panier ne peut pas être modifié : retirez-le avec la corbeille, puis refaites l'inscription avec les bonnes informations.
                  </Typography>
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828', mb: 1 }}>
                    {item.formationTitre}
                  </Typography>
                  {item.nom_du_groupe && (
                    <Typography variant="caption" sx={{ display: 'inline-block', backgroundColor: 'rgba(198,40,40,0.08)', color: '#c62828', px: 1.5, py: 0.25, borderRadius: 10, fontWeight: 600, mb: 0.5 }}>
                      Groupe : {item.nom_du_groupe}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    Session: {item.sessionInfo?.ville || 'Non spécifiée'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.sessionInfo?.date_debut} - {item.sessionInfo?.date_fin}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  {item.rabais && item.formation?.tarif_original && (
                    <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999' }}>
                      {item.formation.tarif_original.toFixed(2)} $
                    </Typography>
                  )}
                  {item.formation?.tarif && (
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {item.formation.tarif.toFixed(2)} $
                    </Typography>
                  )}
                  {item.rabais && (
                    <Typography variant="caption" sx={{ backgroundColor: '#fff8e1', color: '#f57f17', px: 1, py: 0.25, borderRadius: 1, fontWeight: 600 }}>
                      −{item.rabais.pourcentage}% ({item.rabais.code})
                    </Typography>
                  )}
                  <IconButton
                    color="error"
                    onClick={() => removeFromCart(item.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Button
                size="small"
                onClick={() => toggleItemDetails(item.id)}
                endIcon={expandedItems[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none', color: '#666', px: 0 }}
              >
                {expandedItems[item.id] ? 'Masquer les détails' : 'Voir les détails de l\'apprenant'}
              </Button>

              <Collapse in={!!expandedItems[item.id]} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />

              {/* Détails de l'apprenant */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Apprenant
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="body2" color="textSecondary">Nom</Typography>
                  <Typography variant="body2">{item.formData.nom} {item.formData.prenom}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body2">{item.formData.email}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="body2" color="textSecondary">Téléphone</Typography>
                  <Typography variant="body2">{item.formData.telephone}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '280px' }}>
                  <Typography variant="body2" color="textSecondary">Date de naissance</Typography>
                  <Typography variant="body2">{formatDate(item.formData.date_naissance)}</Typography>
                </Box>
              </Box>

              {/* Détails du payeur si applicable */}
              {!item.isForSelf && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Payeur
                  </Typography>
                  <Typography variant="body2">
                    {item.formData.nom_payeur} {item.formData.prenom_payeur}
                  </Typography>
                </>
              )}

              {/* NOUVEAU : Informations supplémentaires si applicable */}
              {!item.isForSelf && 
              (texteTelephones(item.formData.telephone_urgence) || 
               item.formData.allergies || 
               item.formData.besoins_particuliers || 
               item.formData.ecole_frequentee) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Informations supplémentaires
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {texteTelephones(item.formData.telephone_urgence) && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">Téléphone d'urgence</Typography>
                        <Typography variant="body2">{texteTelephones(item.formData.telephone_urgence)}</Typography>
                      </Box>
                    )}
                    {item.formData.allergies && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">Allergies</Typography>
                        <Typography variant="body2">{item.formData.allergies}</Typography>
                      </Box>
                    )}
                    {item.formData.besoins_particuliers && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">Besoins particuliers</Typography>
                        <Typography variant="body2">{item.formData.besoins_particuliers}</Typography>
                      </Box>
                    )}
                    {item.formData.ecole_frequentee && (
                      <Box>
                        <Typography variant="body2" color="textSecondary">École</Typography>
                        <Typography variant="body2">{item.formData.ecole_frequentee}</Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Box>

      </Box>{/* fin colonne gauche */}

      {/* COLONNE DROITE : résumé collant + paiement */}
      <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0, position: { md: 'sticky' }, top: { md: 24 } }}>

      {/* RÉSUMÉ ET ACTIONS */}
      <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Résumé
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1">Nombre de formations:</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{cartItems.length}</Typography>
        </Box>
        
        {(() => {
          const subtotal = cartItems.reduce((sum, item) => sum + (item.formation?.tarif || 0), 0);
          const taxBreakdown = calculateTaxes(subtotal);
          
          return (
            <>
       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pl: 2 }}>
  <Typography variant="body2" color="textSecondary">Sous-total:</Typography>
  <Typography variant="body2">
    {taxBreakdown.subtotal.toFixed(2)} $
  </Typography>
</Box>

<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pl: 2 }}>
  <Typography variant="body2" color="textSecondary">
    TPS (743808768RT0001) 5%:
  </Typography>
  <Typography variant="body2">
    {taxBreakdown.tps.toFixed(2)} $
  </Typography>
</Box>

<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pl: 2 }}>
  <Typography variant="body2" color="textSecondary">
    TVQ (1232577076TQ0001) 9,975%:
  </Typography>
  <Typography variant="body2">
    {taxBreakdown.tvq.toFixed(2)} $
  </Typography>
</Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Montant total à payer:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#c62828' }}>
                  {taxBreakdown.total.toFixed(2)} $
                </Typography>
              </Box>
            </>
          );
        })()}
        
        <Divider sx={{ my: 2 }} />

        <FormControl sx={{ mb: 1 }}>
          <FormLabel sx={{ fontWeight: 600, mb: 1, color: '#333' }}>Méthode de paiement</FormLabel>
          <RadioGroup
            value={paymentMethod}
            onChange={(e) => { setPaymentMethod(e.target.value); setErreursArticles({}); setError(''); }}
          >
            <FormControlLabel
              value="interac"
              control={<Radio />}
              label="Virement Interac"
            />
            {stripeEnabled && (
              <FormControlLabel
                value="stripe"
                control={<Radio />}
                label="Carte bancaire"
              />
            )}
          </RadioGroup>
        </FormControl>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
          {paymentMethod === 'stripe'
            ? 'Vous serez redirigé vers la page de paiement sécurisée Stripe. Vos inscriptions seront confirmées automatiquement après paiement.'
            : 'Confirmez votre inscription et finalisez votre paiement par Interac.'}
        </Typography>

        {/* BOUTONS empilés dans le résumé */}
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          onClick={ouvrirConfirmation}
          disabled={loading || verificationEnCours || cartItems.length === 0}
          sx={{ mb: 1.5 }}
        >
          {verificationEnCours ? <CircularProgress size={24} /> : 'Confirmer'}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => navigate(-1)}
        >
          Continuer vos inscriptions
        </Button>
      </Paper>

      </Box>{/* fin colonne droite */}
      </Box>{/* fin layout 2 colonnes */}

      {/* BARRE FIXE MOBILE : total + Confirmer (petits écrans seulement) */}
      <Paper
        elevation={8}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
          alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, borderRadius: 0,
        }}
      >
        <Box>
          <Typography variant="caption" color="textSecondary">Total à payer</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#c62828', lineHeight: 1 }}>
            {cartTaxes.total.toFixed(2)} $
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={ouvrirConfirmation}
          disabled={loading || verificationEnCours || cartItems.length === 0}
        >
          {verificationEnCours ? <CircularProgress size={24} /> : 'Confirmer'}
        </Button>
      </Paper>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle sx={{ color: '#c62828', fontWeight: 600 }}>
          Confirmation de l'inscription
        </DialogTitle>
        <DialogContent sx={{ minWidth: { sm: 460 } }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir confirmer votre inscription pour {cartItems.length} formation{cartItems.length > 1 ? 's' : ''} ?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {paymentMethod === 'stripe'
              ? 'Vous allez être redirigé vers la page de paiement sécurisée Stripe pour régler par carte.'
              : 'Finalisez votre paiement par Interac. Un courriel de confirmation vous sera envoyé.'}
          </Typography>

          {/* FACTURER À (Interac) : la facture est émise au nom de la personne qui paie */}
          {paymentMethod === 'interac' && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ fontWeight: 600, color: '#333' }}>Facturer à</Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                La personne ou l'organisme qui paie. La facture sera envoyée à ce courriel.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField id="payeur-prenom" size="small" label="Prénom" value={payeur.prenom} onChange={changerPayeur('prenom')} />
                <TextField
                  id="payeur-nom"
                  size="small"
                  label="Nom"
                  value={payeur.nom}
                  onChange={changerPayeur('nom')}
                  error={Boolean(erreursPayeur.nom)}
                />
                <TextField
                  id="payeur-organisme"
                  size="small"
                  label="Organisme (facultatif)"
                  placeholder="Ex. : CPE, entreprise"
                  value={payeur.organisme}
                  onChange={changerPayeur('organisme')}
                  sx={{ gridColumn: '1 / -1' }}
                />
                <TextField
                  id="payeur-courriel"
                  size="small"
                  type="email"
                  label="Courriel"
                  value={payeur.courriel}
                  onChange={changerPayeur('courriel')}
                  error={Boolean(erreursPayeur.courriel)}
                />
                <TextField id="payeur-telephone" size="small" label="Téléphone" value={payeur.telephone} onChange={changerPayeur('telephone')} />
              </Box>
              {(erreursPayeur.nom || erreursPayeur.courriel) && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                  {erreursPayeur.nom || erreursPayeur.courriel}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={confirmerDialogue}
            color="success"
            variant="contained"
            disabled={loading}
          >
            {loading
              ? <CircularProgress size={24} />
              : (paymentMethod === 'stripe' ? 'Payer par carte' : 'Confirmer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Cart;
